import React, { useEffect, useRef, useState } from "react";
import Header from "../../components/Header/Header";
import s from "./AdminUsers.module.scss";
import { Table, TableColumnsType, Tooltip } from "antd";
import { ColumnsType, TableProps } from "antd/es/table";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import {
  getAdminUserDetails,
  getAdminUsersList,
  getUserDetails,
  setUsersCurrentPage,
} from "../../redux/UserSlice";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
} from "@ant-design/icons";
import Footer from "../../components/Footer/Footer";
import { Link, useLocation } from "react-router-dom";
import useWindowDimensions from "../../components/WindowDimensions/WindowDimensions";
import MobileHeader from "../../components/MobileHeader/MobileHeader";
import MobileUsersListTable from "../MobileUsersListTable/MobileUsersListTable";
import { useTranslation } from "react-i18next";
import { t } from "i18next";

interface TableType {
  key?: number;
  number?: number;
  name: string;
  company: string;
  email: string;
  phone: string;
  role: string;
  country?: string;
  is_active: string;
  email_verified: string;
  details?: React.ReactNode;
  children?: TableType[];
}

const DetailsCell = ({ userId }: { userId: number }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();

  return (
    <Link
      to={`/admin/user/${userId}`}
      className={s.edit}
      onClick={() => dispatch(getAdminUserDetails(userId))}
    >
      <Tooltip title={t("Просмотр/Редактирование")}>
        <EditOutlined />
      </Tooltip>
    </Link>
  );
};

const AdminUsers = () => {
  const usersList = useSelector(
    (state: RootState) => state.user.managerWithBrokers
  );
  const [users, setUsers] = useState(usersList);
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const { width } = useWindowDimensions();
  const [pagination, setPagination] = useState({
    current: users?.pagination?.current_page || 1,
    pageSize: users?.pagination?.page_size || 30,
    total: users?.pagination?.total_items || 100,
  });
  const [currentPage, setCurrentPage] = useState<number>(
    users?.pagination?.current_page || 1
  );
  const location = useLocation();
  const expandedManagerId = location.state?.expandedManagerId;
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);

  useEffect(() => {
    if (expandedManagerId !== undefined && expandedManagerId !== null) {
      setExpandedKeys([expandedManagerId]);
    }
  }, [expandedManagerId]);

  const columns: TableColumnsType<TableType> = [
    {
      title: "№",
      dataIndex: "number",
      key: "number",
      width: 50,
      align: "center",
    },
    {
      title: t("ФИО пользователя"),
      key: `name`,
      dataIndex: "name",
      width: 300,
    },
    {
      title: t("Компания"),
      key: `company`,
      dataIndex: "company",
      width: 200,
    },
    {
      title: t("Страна"),
      key: `country`,
      dataIndex: "country",
      width: 120,
    },
    {
      title: t("Email"),
      key: `email`,
      dataIndex: "email",
      width: 200,
    },
    {
      title: t("Телефон"),
      key: `phone`,
      dataIndex: "phone",
      width: 200,
    },

    {
      title: t("Роль"),
      key: `role`,
      dataIndex: "role",
      width: 50,
      align: "center",
    },
    {
      title: t("Активный"),
      key: `is_active`,
      dataIndex: "is_active",
      width: 50,
      align: "center",
      render: (text) => (
        <div className={s.font}>
          {text === t("Да") ? (
            <CheckCircleOutlined className={s.green} />
          ) : (
            <CloseCircleOutlined className={s.red} />
          )}
        </div>
      ),
    },
    {
      title: t("Верифицирован"),
      key: `email_verified`,
      dataIndex: "email_verified",
      width: 50,
      align: "center",
      render: (text) => (
        <div className={s.font}>
          {text === t("Да") ? (
            <CheckCircleOutlined className={s.green} />
          ) : (
            <CloseCircleOutlined className={s.red} />
          )}
        </div>
      ),
    },
    {
      title: t("Просмотр"),
      key: `details`,
      dataIndex: "details",
      align: "center",
      width: 20,
    },
  ];

  useEffect(() => {
    if (usersList) {
      setUsers(usersList);
    }
  }, [dispatch, usersList]);

  const currentPageRef = useRef(currentPage);

  useEffect(() => {
    currentPageRef.current = currentPage;
    dispatch(setUsersCurrentPage(currentPage));
  }, [currentPage]);

  const getNextPage = async (page: number) => {
    setCurrentPage(page);
    dispatch(setUsersCurrentPage(page));

    currentPageRef.current = page;

    if (page === undefined || page === null) return;

    try {
      const res = await dispatch(getAdminUsersList(page));
      if (!res.payload || !("results" in res.payload)) {
        console.error("Unexpected API response:", res);
        return;
      }

      if (res.type === "users/getAdminUsersList/fulfilled") {
        if ("results" in res.payload) {
          // setData(res.payload.results);
          setPagination({
            current: res?.payload?.pagination?.current_page,
            pageSize: res?.payload?.pagination.page_size,
            total: res.payload.pagination.total_items,
          });
        } else {
          console.error("Unexpected API response:", res.payload);
        }
      }
    } catch (error) {
      console.error("Error in getNextPage:", error);
    }
  };

  type TableRowSelection<T extends object = object> =
    TableProps<T>["rowSelection"];

  const rowSelection: TableRowSelection<TableType> = {
    onChange: (selectedRowKeys, selectedRows, info) => {
      console.log(
        `selectedRowKeys: ${selectedRowKeys}`,
        "selectedRows: ",
        selectedRows,
        "info",
        info
      );
    },
    onSelect: (record, selected, selectedRows) => {
      console.log(record, selected, selectedRows);
    },
  };

  return (
    <div>
      {width > 985 ? <Header /> : <MobileHeader />}
      <div className={s.content}>
        <h2 className={s.h2}>{t("Пользователи")}</h2>
        <div className={s.users}>
          {width > 750 ? (
            <Table<TableType>
              columns={columns}
              pagination={{
                ...pagination,
                size: width > 900 ? "default" : "small",
              }}
              rowClassName={(record) => (record.children ? s.childRow : "")}
              expandable={{
                rowExpandable: (record) =>
                  !!record.children && record.children.length > 0,
                expandedRowKeys: expandedKeys,
                onExpand: (expanded, record) => {
                  if (expanded && record.key) {
                    setExpandedKeys([record.key]); // развернули только этот
                  } else {
                    setExpandedKeys([]);
                  }
                },
              }}
              dataSource={users?.results.map((it, index) => ({
                key: it.manager.id,
                number: index + 1,
                name: it.manager.full_name,
                company: it.manager.company_name,
                email: it.manager.email,
                phone: it.manager.phone,
                country: `${it.manager.registered_country_name}`,
                role: it.manager.role,
                is_active: it.manager.is_active ? "Да" : "Нет",
                email_verified: it.manager.email_verified ? "Да" : "Нет",
                details: <DetailsCell userId={it.manager.id} />,
                children: it.brokers.map((broker) => ({
                  key: broker.id,
                  name: broker.full_name,
                  company: broker.company_name,
                  email: broker.email,
                  country: `${broker.registered_country_name}`,
                  phone: broker.phone,
                  role: broker.role,
                  is_active: broker.is_active ? "Да" : "Нет",
                  email_verified: broker.email_verified ? "Да" : "Нет",
                  details: <DetailsCell userId={broker.id} />,
                })),
              }))}
              onChange={(pagination) => {
                if (pagination?.current) {
                  getNextPage(pagination.current);
                }
              }}
            />
          ) : (
            <MobileUsersListTable />
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AdminUsers;
